import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fieldGuideParts } from "@/data/field-guide/field-guide-data";
import { useViewTransitionNavigate } from "@/lib/hooks/use-view-transition";
import { BookOpen, ChevronRight } from "lucide-react";

export default function FieldGuideOverview() {
    const [, navigate] = useViewTransitionNavigate();

    return (
        <div className="flex flex-col h-full overflow-y-auto">
            <div className="flex-1 p-6 space-y-6">
                {/* Header */}
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-primary/10 rounded-lg">
                            <BookOpen className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">The Field Guide</h1>
                            <p className="text-muted-foreground">Learn the methods behind the dig</p>
                        </div>
                    </div>
                    
                    <Card className="border-primary/20 bg-primary/5">
                        <CardContent className="pt-6">
                            <p className="text-base leading-relaxed">
                                Welcome to the Field Guide—your companion for understanding <em>why</em> Bible Glen 
                                is designed the way it is and <em>how</em> to become a better student of Scripture. 
                                This is the "methods tent" of the dig site, where we teach the principles behind the tools.
                            </p>
                            <p className="text-base leading-relaxed">
                                <strong>NOTE:</strong> This was partially written by AI, but it will eventually be written by a human.
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Parts */}
                <div className="space-y-6">
                    {fieldGuideParts.map((part) => (
                        <Card key={part.number} className="overflow-hidden hover:shadow-lg transition-shadow">
                            <CardHeader className="bg-muted/50">
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="font-mono">
                                                Part {part.number}
                                            </Badge>
                                            <CardTitle className="text-xl">{part.title}</CardTitle>
                                        </div>
                                        <CardDescription className="text-base italic">
                                            {part.subtitle}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="space-y-3">
                                    {part.articles.map((article) => (
                                        <button
                                            key={article.id}
                                            onClick={() => navigate(`/app/field-guide/${article.id}`)}
                                            className="w-full flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-accent transition-all group text-left"
                                        >
                                            <div className="flex items-start gap-3 flex-1">
                                                <Badge 
                                                    variant="secondary" 
                                                    className="font-mono mt-0.5 shrink-0"
                                                >
                                                    {article.articleNumber}
                                                </Badge>
                                                <div className="space-y-1 flex-1">
                                                    <div className="font-semibold group-hover:text-primary transition-colors">
                                                        {article.title}
                                                    </div>
                                                    {article.subtitle && (
                                                        <div className="text-sm text-muted-foreground">
                                                            {article.subtitle}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Footer Note */}
                <Card className="border-muted">
                    <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            <strong>Note:</strong> The Field Guide is optional reading designed to deepen your 
                            understanding of biblical interpretation. Feel free to explore these articles at your 
                            own pace, or jump straight into using the app's tools.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

